<template>
  <v-app>
    <v-container class="fill-height">
      <v-row
        justify="center"
        align="center"
      >
        <v-col
          cols="12"
          sm="8"
          md="6"
          lg="4"
          class="text-center"
        >
          <EmailVerificationCard
            v-if="isEmailVerificationPending"
            @return="handleReturn"
          />

          <AuthErrorCard
            v-else-if="error"
            :error="error"
            @return="handleReturn"
          />

          <div
            v-else
            class="d-flex flex-column align-center"
          >
            <v-img
              :src="brand.logo"
              width="180"
              class="mb-8"
            />
            
            <v-progress-circular
              indeterminate
              color="primary"
              size="32"
              class="mb-4"
            />
            
            <h2 class="text-h6 font-weight-medium text-medium-emphasis">
              Completing Authentication
            </h2>
            <p class="text-body-2 text-medium-emphasis mt-2">
              Please wait while we verify your credentials
            </p>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import EmailVerificationCard from '@/components/auth/EmailVerificationCard.vue'
import AuthErrorCard from '@/components/auth/AuthErrorCard.vue'
import { brand } from '@/config/brand'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const error = ref<string | null>(null)

const isEmailVerificationPending = computed(() => {
  const errorType = route.query.error as string
  const errorDescription = route.query.error_description as string
  return errorType === 'access_denied' && 
         errorDescription?.toLowerCase().includes('verify your email')
})

onMounted(async () => {
  if (route.query.error) {
    const errorDescription = route.query.error_description as string
    if (!isEmailVerificationPending.value) {
      error.value = errorDescription || 'Authentication failed'
    }
  } else {
    try {
      await authStore.handleAuth0Callback(route.query.code as string, route.query.state as string)
      router.push((route.query.redirect as string) || '/')
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Authentication failed'
    }
  }
})

const handleReturn = async () => {
  await authStore.logout()
}
</script>

<style scoped>
.auth-card {
  border-radius: 16px;
}

:deep(.v-alert) {
  border-radius: 8px;
}
</style>
